import { supabase } from '../supabaseClient';

/**
 * 輕量 ticket 搜尋（autocomplete 用）
 * @param {string} query - `#` 後面的輸入（數字或文字）
 * @param {string} companyId
 * @param {number} limit
 */
export const searchTicketsByPrefix = async (query, companyId, limit = 8) => {
  if (!query || !companyId) return [];

  try {
    let dbQuery = supabase
      .from('tickets')
      .select('id, ticket_no, title, status, projects:project_id(name, color)')
      .eq('company_id', companyId)
      .limit(limit);

    const num = parseInt(query, 10);
    if (!isNaN(num) && String(num) === query) {
      // 純數字：前綴搜尋（"3" → #3, #30-39, #300-399...）
      const conditions = [];
      let multiplier = 1;
      for (let i = 0; i < 4; i++) {
        const lower = num * multiplier;
        const upper = (num + 1) * multiplier;
        conditions.push(`and(ticket_no.gte.${lower},ticket_no.lt.${upper})`);
        multiplier *= 10;
      }
      dbQuery = dbQuery.or(conditions.join(',')).order('ticket_no', { ascending: true });
    } else {
      // 文字：以 title 模糊搜尋
      dbQuery = dbQuery.ilike('title', `%${query}%`).order('ticket_no', { ascending: false });
    }

    const { data, error } = await dbQuery;
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error searching tickets by prefix:', error);
    return [];
  }
};

/**
 * 從文字中提取所有 #\d+ ticket_no
 */
const extractTicketNos = (text) => {
  if (!text) return [];
  const matches = [...text.matchAll(/(^|[^\d])#(\d+)/gm)];
  return [...new Set(matches.map((m) => parseInt(m[2], 10)))];
};

/**
 * 同步 ticket 的引用關係（全量比對）
 * 從 ticket 的 description + 所有 comments 提取 #\d+，與 DB 比對後增刪
 *
 * @param {number} ticketId
 * @param {string} companyId
 * @param {string} userId - 操作者
 */
export const syncTicketReferences = async (ticketId, companyId, userId) => {
  if (!ticketId || !companyId) return;

  try {
    // 1. 取得 ticket description + ticket_no
    const { data: ticket } = await supabase
      .from('tickets')
      .select('description, ticket_no')
      .eq('id', ticketId)
      .single();

    // 2. 取得所有 comments
    const { data: comments } = await supabase
      .from('ticket_comments')
      .select('content')
      .eq('ticket_id', ticketId);

    // 3. 提取所有 #\d+
    const allText = [ticket?.description || '', ...(comments || []).map((c) => c.content)].join('\n');
    let ticketNos = extractTicketNos(allText);

    // 排除自己
    if (ticket?.ticket_no) {
      ticketNos = ticketNos.filter((no) => no !== ticket.ticket_no);
    }

    // 4. 取得現有 references
    const { data: existing } = await supabase
      .from('ticket_references')
      .select('target_ticket_no')
      .eq('source_ticket_id', ticketId);

    const existingNos = new Set((existing || []).map((r) => r.target_ticket_no));
    const newNos = ticketNos.filter((n) => !existingNos.has(n));
    const removedNos = [...existingNos].filter((n) => !ticketNos.includes(n));

    // 5. Insert new
    if (newNos.length > 0) {
      await supabase.from('ticket_references').insert(
        newNos.map((no) => ({
          source_ticket_id: ticketId,
          target_ticket_no: no,
          company_id: companyId,
          created_by: userId,
        }))
      );
    }

    // 6. Delete removed
    if (removedNos.length > 0) {
      await supabase
        .from('ticket_references')
        .delete()
        .eq('source_ticket_id', ticketId)
        .in('target_ticket_no', removedNos);
    }
  } catch (error) {
    console.error('Error syncing ticket references:', error);
  }
};

/**
 * 取得 ticket 的所有關聯（正向 + 反向）
 *
 * @param {number} ticketId
 * @param {number} ticketNo - 此 ticket 的 ticket_no
 * @param {string} companyId
 * @returns {{ data: Array, error: any }}
 */
export const getTicketReferences = async (ticketId, ticketNo, companyId) => {
  if (!ticketId || !companyId) return { data: [] };

  try {
    // 正向：此 ticket 引用的其他 ticket
    const { data: outgoing } = await supabase
      .from('ticket_references')
      .select('target_ticket_no')
      .eq('source_ticket_id', ticketId);

    // 反向：引用此 ticket 的其他 ticket
    const { data: incoming } = await supabase
      .from('ticket_references')
      .select('source_ticket_id')
      .eq('company_id', companyId)
      .eq('target_ticket_no', ticketNo);

    // 收集需要查詢的 ticket_no（正向）和 ticket_id（反向）
    const outgoingNos = (outgoing || []).map((r) => r.target_ticket_no);
    const incomingIds = (incoming || []).map((r) => r.source_ticket_id);

    // 批次查詢 ticket 詳情
    const allTicketNos = [...outgoingNos];
    const allTicketIds = [...incomingIds];

    let ticketsByNo = {};
    let ticketsById = {};

    if (allTicketNos.length > 0) {
      const { data } = await supabase
        .from('tickets')
        .select('id, ticket_no, title, status, projects:project_id(name, color)')
        .eq('company_id', companyId)
        .in('ticket_no', allTicketNos);
      (data || []).forEach((t) => { ticketsByNo[t.ticket_no] = t; });
    }

    if (allTicketIds.length > 0) {
      const { data } = await supabase
        .from('tickets')
        .select('id, ticket_no, title, status, projects:project_id(name, color)')
        .in('id', allTicketIds);
      (data || []).forEach((t) => { ticketsById[t.id] = t; });
    }

    // 組合結果（去重：如果 A→B 且 B→A，只顯示一次）
    const seen = new Set();
    const results = [];

    outgoingNos.forEach((no) => {
      const t = ticketsByNo[no];
      if (t && !seen.has(t.id)) {
        seen.add(t.id);
        results.push(t);
      }
    });

    incomingIds.forEach((id) => {
      const t = ticketsById[id];
      if (t && !seen.has(t.id)) {
        seen.add(t.id);
        results.push(t);
      }
    });

    return { data: results };
  } catch (error) {
    console.error('Error fetching ticket references:', error);
    return { data: [] };
  }
};
